import { Props as ETProps } from "@hooks/useElapsedTime"
import { useElapsedTime } from "@hooks/useElapsedTime"
import { defaultEasing, Easing, getEasing } from "@utils/easing"
import { ReactNode } from "react"

export interface Props {
  /** Play and pause counting animation. Default: false */
  isCounting?: boolean
  /** Initial value. Default: 0 */
  start?: number
  /** Target value */
  end?: number
  /** Animation duration in seconds. Default to 2 if end is set*/
  duration?: number
  /** Number of decimal places after the decimal separator. Default: 0 */
  decimalPlaces?: number
  /** Decimal separator character. Default: "." */
  decimalSeparator?: string
  /** Thousands separator character. Default: "" */
  thousandsSeparator?: string
  /** On animation complete event handler */
  onComplete?: ETProps["onComplete"]
  /**  Easing function to control how the animation is progressing. Default: easeOutExpo */
  easing?: Easing
  /** Function that formats the output value */
  formatter?: (value: number) => ReturnValue
  /** Update interval in seconds. Determines how often the animated value will change. When set to 0 the value will update on each key frame. Default: 0 */
  updateInterval?: number
  /** On value update event handler. It receives the current value */
  onUpdate?: (value: ReturnValue) => void
}

export type ReturnValue = number | string | ReactNode

export interface ReturnProps {
  /** Current value of the count up animation */
  value: ReturnValue
  /** Method to start over the animation*/
  reset: () => void
}

const getDuration = (end?: number, duration?: number) => {
  if (typeof end !== "number") {
    return undefined
  }

  return typeof duration === "number" ? duration : 2
}

const addThousandsSeparator = (value: string, separator: string) =>
  value.replace(/\B(?=(\d{3})+(?!\d))/g, separator)

const getDecimalPartLength = (num: number) =>
  (num.toString().split(".")[1] || "").length

const getDefaultDecimalPlaces = (start: number, end?: number) => {
  const startDecimals = getDecimalPartLength(start)
  const endDecimals = getDecimalPartLength(end || 1)

  return startDecimals >= endDecimals ? startDecimals : endDecimals
}

export const useCountUp = ({
  isCounting = false,
  start = 0,
  end,
  duration,
  decimalPlaces = getDefaultDecimalPlaces(start, end),
  decimalSeparator = ".",
  thousandsSeparator = "",
  onComplete,
  easing = defaultEasing,
  formatter,
  updateInterval,
  onUpdate,
}: Props): ReturnProps => {
  const durationValue = getDuration(end, duration)
  const getValue = (elapsedTime: number) => {
    let rawValue

    if (durationValue === 0 && typeof end === "number") {
      rawValue = end
    } else if (typeof end === "number" && typeof durationValue === "number") {
      const easingFn = getEasing(easing)
      // elapsedTime should always be less or equal to the durationValue
      const time = elapsedTime < durationValue ? elapsedTime : durationValue
      rawValue = easingFn(time, start, end - start, durationValue)
    } else {
      rawValue = start + elapsedTime
    }

    // Return value after formatting it
    if (typeof formatter === "function") {
      return formatter(rawValue)
    }

    if (decimalPlaces === 0) {
      const valueStr = Math.round(rawValue).toString()
      return addThousandsSeparator(valueStr, thousandsSeparator)
    }

    const [int, decimals] = rawValue.toFixed(decimalPlaces).split(".")
    const intFormatted = addThousandsSeparator(int, thousandsSeparator)
    return `${intFormatted}${decimalSeparator}${decimals}`
  }

  const { elapsedTime, reset } = useElapsedTime({
    isPlaying: isCounting,
    duration: durationValue,
    onComplete,
    updateInterval,
    onUpdate:
      typeof onUpdate === "function"
        ? (currentTime: number) => onUpdate(getValue(currentTime))
        : undefined,
  })

  return { value: getValue(elapsedTime), reset }
}
